import React, { useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle2, ShieldCheck, Loader2, Sparkles, Building2, Globe, Clock } from 'lucide-react';
import { Logo } from './Logo';
import { sendProjectScopingBrief } from '../../services/emailService';

interface StartProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultService?: string;
}

export const StartProjectModal: React.FC<StartProjectModalProps> = ({
  isOpen,
  onClose,
  defaultService = 'Website Development',
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedAppId, setSubmittedAppId] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Form data across 5 steps
  const [formData, setFormData] = useState({
    // Step 1: Service
    service: defaultService || 'Website Development',
    // Step 2: Project Scope
    projectGoal: '',
    projectType: 'New Project (Build from Scratch)',
    targetTimeline: '1 - 2 Months',
    // Step 3: Budget & Currency
    currency: 'USD ($)',
    budgetRange: '$5,000 - $15,000',
    // Step 4: Contact & Country
    fullName: '',
    email: '',
    phone: '',
    company: '',
    country: 'United States 🇺🇸',
    websiteUrl: '',
    preferredCommunication: 'Email & Scheduled Video Call',
    // Step 5: Notes
    additionalNotes: '',
    ndaConsent: true,
  });

  if (!isOpen) return null;

  const handleNext = () => {
    setFormError(null);
    if (currentStep === 1) {
      if (!formData.service) {
        setFormError('Please select a primary service.');
        return;
      }
    }
    if (currentStep === 2) {
      if (!formData.projectGoal.trim()) {
        setFormError('Please describe your core project objectives or current challenges.');
        return;
      }
    }
    if (currentStep === 4) {
      if (!formData.fullName.trim() || !formData.email.trim()) {
        setFormError('Please provide your name and business email.');
        return;
      }
    }
    setCurrentStep((prev) => Math.min(prev + 1, 5));
  };

  const handleBack = () => {
    setFormError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.email.trim()) {
      setFormError('Please provide your name and email.');
      return;
    }

    setFormError(null);
    setIsSubmitting(true);

    try {
      const res = await sendProjectScopingBrief(formData);
      setIsSubmitting(false);
      if (res.success) {
        const refId = res.leadId || 'EBON-PRJ-' + Math.floor(10000 + Math.random() * 90000);
        setSubmittedAppId(refId);
      } else {
        setFormError(res.error || 'Submission failed. Please email info@ebonmedia.com directly.');
      }
    } catch (err: any) {
      setIsSubmitting(false);
      setFormError('Network error. Please email your project details directly to info@ebonmedia.com.');
    }
  };

  const handleReset = () => {
    setSubmittedAppId(null);
    setCurrentStep(1);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050A13]/85 backdrop-blur-md animate-fade-in" role="dialog" aria-modal="true">
      <div className="relative w-full max-w-2xl bg-[#071426] border border-[#1b2f4a] rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        
        {/* Header with Step Progress */}
        <div className="px-6 py-5 border-b border-[#1b2f4a] bg-[#0B2342]/50 flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono font-bold text-[#FF851B] uppercase tracking-wider">
                Guided International Scoping
              </span>
              <span className="text-[11px] text-[#8FA0B8] bg-[#050A13] px-2 py-0.5 rounded border border-[#1b2f4a]">
                Step {currentStep} of 5
              </span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white font-display">
              {currentStep === 1 && 'Step 1: Choose Your Core Service'}
              {currentStep === 2 && 'Step 2: Describe Your Project Objectives'}
              {currentStep === 3 && 'Step 3: Select Your Budget Range'}
              {currentStep === 4 && 'Step 4: Share Your Contact & Location'}
              {currentStep === 5 && 'Step 5: Review & Submit Project Brief'}
            </h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 text-[#8FA0B8] hover:text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Close project scoping modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Step Progress Bar */}
        <div className="w-full h-1 bg-[#050A13]">
          <div 
            className="h-full bg-gradient-to-r from-[#FF5A36] to-[#159DE8] transition-all duration-300"
            style={{ width: `${(currentStep / 5) * 100}%` }}
          />
        </div>

        {/* Modal Body */}
        <div className="p-6 sm:p-8 overflow-y-auto flex-grow space-y-6">
          {submittedAppId ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-green-500/20 text-[#00C853] rounded-full flex items-center justify-center border border-green-500/40">
                <CheckCircle2 size={36} />
              </div>
              <h4 className="text-2xl font-bold text-white font-display">Project Scoping Brief Dispatched!</h4>
              <p className="text-sm text-[#D5DFEC] max-w-md mx-auto leading-relaxed">
                Thank you, <strong className="text-white">{formData.fullName}</strong>. Your project brief has been securely logged. Your tracking Reference ID is:
              </p>

              <div className="inline-block px-4 py-2 rounded-xl bg-[#050A13] border border-[#159DE8] text-sm font-mono font-bold text-[#159DE8]">
                {submittedAppId}
              </div>

              <div className="p-4 bg-[#0B2342]/40 border border-[#1b2f4a] rounded-2xl max-w-md mx-auto text-left text-xs space-y-2 text-[#D5DFEC]">
                <div className="flex justify-between">
                  <span className="text-[#8FA0B8]">Selected Domain:</span>
                  <strong className="text-white">{formData.service}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8FA0B8]">Target Country:</span>
                  <strong className="text-white">{formData.country}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#8FA0B8]">Target Budget:</span>
                  <strong className="text-[#00C853]">{formData.budgetRange} ({formData.currency})</strong>
                </div>
                <div className="pt-2 border-t border-[#1b2f4a]/60 text-[#8FA0B8]">
                  Our lead architects will review your requirements and respond at <strong className="text-[#159DE8]">{formData.email}</strong> with an architectural scoping outline within 24 business hours.
                </div>
              </div>

              <button
                type="button"
                onClick={handleReset}
                className="mt-4 px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white font-bold text-xs hover:shadow-glow-orange transition-all"
              >
                Close & Return to Website
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {formError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs">
                  {formError}
                </div>
              )}

              {/* STEP 1: SERVICE SELECTION */}
              {currentStep === 1 && (
                <div className="space-y-4">
                  <p className="text-xs text-[#8FA0B8]">
                    Select the primary capability your business is looking to commission:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { id: 'Website Development', desc: 'Custom, Corporate B2B, Speed & Maintenance' },
                      { id: 'SEO & Digital Marketing', desc: 'Technical SEO, Keyword Strategy & Google Ads' },
                      { id: 'AI Solutions & Automation', desc: 'Workflows, Customer Assistants & Vector Search' },
                      { id: 'Custom Software Development', desc: 'Bespoke Web Apps, APIs & Modernization' },
                      { id: 'Hybrid Mobile Applications', desc: 'iOS & Android via React Native / Flutter' },
                      { id: 'ERP & SaaS Solutions', desc: 'Multi-Tenant Cloud Platforms & Operations' },
                    ].map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, service: s.id })}
                        className={`p-3.5 rounded-xl border text-left transition-all ${
                          formData.service === s.id
                            ? 'bg-[#0B2342] border-[#159DE8] ring-1 ring-[#159DE8] shadow-md'
                            : 'bg-[#050A13] border-[#1b2f4a] hover:border-[#159DE8]/50'
                        }`}
                      >
                        <div className="text-xs font-bold text-white">{s.id}</div>
                        <div className="text-[10px] text-[#8FA0B8] mt-0.5">{s.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* STEP 2: PROJECT OBJECTIVES */}
              {currentStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Project Nature *</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        'New Project (Build from Scratch)',
                        'Redesign / Modernization of Existing Platform',
                        'Ongoing Website Care & Digital Marketing Growth',
                        'Technical Audit & Architectural Consulting'
                      ].map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setFormData({ ...formData, projectType: t })}
                          className={`p-3 rounded-xl border text-left text-xs font-medium transition-all ${
                            formData.projectType === t
                              ? 'bg-[#0B2342] border-[#159DE8] text-white font-bold ring-1 ring-[#159DE8]'
                              : 'bg-[#050A13] border-[#1b2f4a] text-[#8FA0B8] hover:text-white'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-1">
                      What are your core objectives or current bottlenecks? *
                    </label>
                    <textarea
                      rows={3}
                      value={formData.projectGoal}
                      onChange={(e) => setFormData({ ...formData, projectGoal: e.target.value })}
                      placeholder="e.g. We need to rebuild our corporate web platform, improve page speeds under 1.5s, integrate an AI customer assistant, and rank for commercial buyer keywords..."
                      className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 focus:outline-none focus:border-[#159DE8] text-xs resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Target Go-Live Timeline</label>
                    <select
                      value={formData.targetTimeline}
                      onChange={(e) => setFormData({ ...formData, targetTimeline: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white text-xs focus:outline-none focus:border-[#159DE8]"
                    >
                      <option value="Urgent (Within 4 Weeks)">Urgent (Within 4 Weeks)</option>
                      <option value="1 - 2 Months">1 - 2 Months</option>
                      <option value="2 - 4 Months">2 - 4 Months</option>
                      <option value="Flexible / Long-Term Partnership">Flexible / Long-Term Partnership</option>
                    </select>
                  </div>
                </div>
              )}

              {/* STEP 3: BUDGET & CURRENCY */}
              {currentStep === 3 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-2 border-b border-[#1b2f4a]">
                    <span className="text-xs text-[#8FA0B8]">Select Invoicing Currency:</span>
                    <div className="inline-flex gap-1 bg-[#050A13] p-1 rounded-lg border border-[#1b2f4a]">
                      {['USD ($)', 'GBP (£)', 'CAD ($)', 'INR (₹)'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setFormData({ ...formData, currency: c })}
                          className={`px-2.5 py-1 rounded text-xs font-bold transition-all ${
                            formData.currency === c
                              ? 'bg-[#FF851B] text-white'
                              : 'text-[#8FA0B8] hover:text-white'
                          }`}
                        >
                          {c}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-2">Estimated Budget Range</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        'Under $3,000 (Starter Scope / Retainer)',
                        '$5,000 - $15,000 (Complete Platform Build)',
                        '$15,000 - $35,000 (Advanced Software / Multi-Tenant)',
                        '$35,000+ (Enterprise System)',
                        'Not Sure Yet (Discuss Scoping First)'
                      ].map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setFormData({ ...formData, budgetRange: b })}
                          className={`p-3 rounded-xl border text-left text-xs transition-all ${
                            formData.budgetRange === b
                              ? 'bg-[#0B2342] border-[#00C853] text-white font-bold ring-1 ring-[#00C853]'
                              : 'bg-[#050A13] border-[#1b2f4a] text-[#8FA0B8] hover:text-white'
                          }`}
                        >
                          {b}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* STEP 4: CONTACT & COUNTRY */}
              {currentStep === 4 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Full Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                        placeholder="John Anderson"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 text-xs focus:outline-none focus:border-[#159DE8]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Business Email *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@enterprise.com"
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 text-xs focus:outline-none focus:border-[#159DE8]"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Target Country / Region *</label>
                      <select
                        value={formData.country}
                        onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white text-xs focus:outline-none focus:border-[#159DE8]"
                      >
                        <option value="United States 🇺🇸">United States 🇺🇸</option>
                        <option value="United Kingdom 🇬🇧">United Kingdom 🇬🇧</option>
                        <option value="Canada 🇨🇦">Canada 🇨🇦</option>
                        <option value="Global / Other Region 🌐">Global / Other Region 🌐</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Company / Organization</label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Acme Inc."
                        className="w-full px-3.5 py-2.5 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 text-xs focus:outline-none focus:border-[#159DE8]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Current Website URL (Optional)</label>
                    <input
                      type="url"
                      value={formData.websiteUrl}
                      onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                      placeholder="https://yourcompany.com"
                      className="w-full px-3.5 py-2 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 text-xs focus:outline-none focus:border-[#159DE8]"
                    />
                  </div>
                </div>
              )}

              {/* STEP 5: REVIEW & FINAL SUBMISSION */}
              {currentStep === 5 && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-[#050A13] border border-[#1b2f4a] space-y-2 text-xs">
                    <div className="font-bold text-white text-sm pb-1 border-b border-[#1b2f4a]">
                      Project Summary Review:
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-[#D5DFEC]">
                      <div><span className="text-[#8FA0B8]">Service:</span> {formData.service}</div>
                      <div><span className="text-[#8FA0B8]">Type:</span> {formData.projectType.split('(')[0]}</div>
                      <div><span className="text-[#8FA0B8]">Target Country:</span> {formData.country}</div>
                      <div><span className="text-[#8FA0B8]">Budget:</span> {formData.budgetRange} ({formData.currency})</div>
                      <div><span className="text-[#8FA0B8]">Client Name:</span> {formData.fullName}</div>
                      <div><span className="text-[#8FA0B8]">Contact Email:</span> {formData.email}</div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#8FA0B8] mb-1">Any Specific Technical Preferences or Notes?</label>
                    <textarea
                      rows={2}
                      value={formData.additionalNotes}
                      onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                      placeholder="e.g. Prefer Next.js, need Stripe integration, interested in monthly SEO retainer..."
                      className="w-full px-3.5 py-2 bg-[#050A13] border border-[#1b2f4a] rounded-lg text-white placeholder-[#8FA0B8]/40 text-xs resize-none"
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs text-[#8FA0B8]">
                    <ShieldCheck size={16} className="text-[#00C853] shrink-0" />
                    <span>Protected under mutual Non-Disclosure Agreement (NDA). We respect client confidentiality.</span>
                  </div>
                </div>
              )}

              {/* Navigation Actions */}
              <div className="pt-4 border-t border-[#1b2f4a] flex items-center justify-between">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="px-4 py-2 rounded-full bg-[#050A13] border border-[#1b2f4a] text-xs font-bold text-[#8FA0B8] hover:text-white transition-all flex items-center gap-1.5"
                  >
                    <ArrowLeft size={13} />
                    <span>Back</span>
                  </button>
                ) : (
                  <div />
                )}

                {currentStep < 5 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold hover:shadow-glow-orange transition-all flex items-center gap-1.5"
                  >
                    <span>Continue to Step {currentStep + 1}</span>
                    <ArrowRight size={13} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="px-7 py-2.5 rounded-full bg-gradient-to-r from-[#FF5A36] to-[#FF851B] text-white text-xs font-bold hover:shadow-glow-orange transition-all flex items-center gap-1.5 disabled:opacity-60 shadow-lg"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        <span>Submitting Project Brief...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Project Brief</span>
                        <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};
